import React, { useState, useEffect, useMemo } from 'react';
import ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';
import { ToWords } from 'to-words';
import { Plus, Trash2, RotateCcw } from 'lucide-react';

const toWords = new ToWords({
    localeCode: 'en-IN',
    converterOptions: { currency: true, ignoreDecimal: false, ignoreZeroCurrency: false },
});

const defaultMyDetails = {
    name: 'Deepayu Ninama',
    email: 'ninamadeepayubipinchandra@gmail.com',
    phone: '+91 72268 03611',
    address: 'C-25, Kalpvruksh Society, New Koba, Gandhinagar, 382007',
    bankName: 'State Bank of India',
    accountNumber: '42975383672',
    ifscCode: 'SBIN0014975',
    upi: 'ninamadeepayu-1@oksbi'
};

const defaultInvoiceDetails = {
    title: 'INVOICE',
    companyName: '',
    companyAddress: '',
    gstNumber: '',
    companyPhone: ''
};

export default function InvoiceGenerator() {
    const [myDetails, setMyDetails] = useState(() => {
        const saved = localStorage.getItem('myInvoiceDetails');
        return saved ? JSON.parse(saved) : defaultMyDetails;
    });

    useEffect(() => {
        localStorage.setItem('myInvoiceDetails', JSON.stringify(myDetails));
    }, [myDetails]);

    const [invoiceDetails, setInvoiceDetails] = useState({
        ...defaultInvoiceDetails,
        date: new Date().toISOString().split('T')[0]
    });

    const [items, setItems] = useState([{ particulars: '', amount: '' }]);
    
    // NEW: Checkbox state for download options
    const [downloadOptions, setDownloadOptions] = useState({ excel: true, pdf: false });

    const handleMyDetailsChange = (e) => setMyDetails({ ...myDetails, [e.target.name]: e.target.value });
    const handleInvoiceChange = (e) => setInvoiceDetails({ ...invoiceDetails, [e.target.name]: e.target.value });

    const handleItemChange = (index, field, value) => {
        const newItems = [...items];
        newItems[index][field] = value;
        setItems(newItems);
    };

    const addItem = () => setItems([...items, { particulars: '', amount: '' }]);
    const removeItem = (index) => setItems(items.filter((_, i) => i !== index));

    const handleReset = () => {
        if (window.confirm("Are you sure you want to clear all fields? This will reset everything to default.")) {
            setMyDetails(defaultMyDetails);
            setInvoiceDetails({
                ...defaultInvoiceDetails,
                date: new Date().toISOString().split('T')[0]
            });
            setItems([{ particulars: '', amount: '' }]);
            localStorage.removeItem('myInvoiceDetails');
        }
    };

    const totalAmount = useMemo(() => {
        return items.reduce((sum, item) => sum + (parseFloat(item.amount) || 0), 0);
    }, [items]);

    const totalInWords = useMemo(() => {
        if (totalAmount === 0) return '';
        return toWords.convert(totalAmount);
    }, [totalAmount]);

    const generateInvoice = async () => {
        // NEW: Checkbox validation logic
        if (!downloadOptions.excel && !downloadOptions.pdf) {
            alert("Please select at least one download format.");
            return;
        }

        if (downloadOptions.pdf) {
            alert("Note: Direct Excel-to-PDF conversion inside the browser requires a backend server. Downloading as Excel will proceed if selected.");
            if (!downloadOptions.excel) return; // Stop execution if ONLY PDF is checked
        }

        try {
            const response = await fetch(`${window.location.origin}/invoice-template.xlsx`);
            
            if (!response.ok || response.headers.get("content-type")?.includes("text/html")) {
                throw new Error("Could not find the Excel file. Check if it's in the public folder and named correctly.");
            }

            const arrayBuffer = await response.arrayBuffer();
            const workbook = new ExcelJS.Workbook();
            await workbook.xlsx.load(arrayBuffer);
            const worksheet = workbook.getWorksheet(1);

            worksheet.getCell('B11').value = myDetails.name;
            worksheet.getCell('B12').value = myDetails.address;
            worksheet.getCell('F4').value = `P: ${myDetails.phone}`;
            worksheet.getCell('B4').value = `E: ${myDetails.email}`;
            worksheet.getCell('C31').value = `${myDetails.bankName}`;
            worksheet.getCell('C32').value = `${myDetails.accountNumber}`;
            worksheet.getCell('C33').value = `${myDetails.ifscCode}`;
            worksheet.getCell('C36').value = `${myDetails.upi}`;

            worksheet.getCell('B2').value = invoiceDetails.title;
            worksheet.getCell('B15').value = invoiceDetails.companyName;
            worksheet.getCell('B16').value = invoiceDetails.companyAddress;
            worksheet.getCell('B9').value = `Date: ${invoiceDetails.date}`;
            if (invoiceDetails.gstNumber) worksheet.getCell('B17').value = `GST: ${invoiceDetails.gstNumber}`;
            if (invoiceDetails.companyPhone) worksheet.getCell('B18').value = `Ph: ${invoiceDetails.companyPhone}`;

            let currentRow = 22; 
            
            items.forEach((item, index) => {
                worksheet.getCell(`B${currentRow}`).value = index + 1; 
                worksheet.getCell(`C${currentRow}`).value = item.particulars; 
                worksheet.getCell(`F${currentRow}`).value = parseFloat(item.amount) || 0; 
                currentRow++;
            });

            const totalRow = Math.max(26, currentRow);
            
            worksheet.getCell(`F${totalRow}`).value = totalAmount;
            
            const formattedWords = totalInWords ? totalInWords.replace('Rupees ', 'INR ') : '';
            worksheet.getCell(`B${totalRow + 1}`).value = formattedWords;

            const buffer = await workbook.xlsx.writeBuffer();
            const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
            saveAs(blob, `${invoiceDetails.companyName.replace(/\s+/g, '_') || 'New'}_Invoice.xlsx`);

        } catch (error) {
            console.error('Error generating invoice:', error);
            alert('Could not generate the invoice. Check your console for details.');
        }
    };

    // Shared input style for consistency
    const inputStyle = "bg-transparent border-b border-zinc-800 pb-3 outline-none text-xl md:text-2xl text-white placeholder-zinc-700 focus:border-zinc-400 transition-colors w-full";
    const labelStyle = "text-sm text-zinc-500 font-medium uppercase tracking-wider mb-2 block";

    return (
        <div className="bg-zinc-900 min-h-screen font-['Montserrat',sans-serif] text-white">
            <div className="max-w-5xl mx-auto px-8 md:px-8 pb-32 pt-48">
                
                {/* Section: Header */}
                <section className="mb-32">
                    <h1 className="text-5xl md:text-8xl text-white leading-tight font-medium [word-spacing:-8px]">
                        Invoice Generator
                    </h1>
                </section>

                <div className="flex flex-col gap-8 w-full">

                    {/* Section: 1 */}
                    <div className="grid gap-8">
                        <hr className="h-px border-0 bg-zinc-800 mt-10" />
                        <div className="flex flex-col md:flex-row gap-8 md:gap-14">
                            <p className="text-xl text-zinc-500 font-semibold">01</p>
                            
                            <div className="grid gap-12 w-full">
                                <h2 className="text-3xl font-medium">Your Details</h2>
                                
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-x-12 gap-y-10">
                                    <div className="md:col-span-2">
                                        <label className={labelStyle}>Name / Business</label>
                                        <input type="text" name="name" value={myDetails.name} onChange={handleMyDetailsChange} className={inputStyle} placeholder="John Doe" />
                                    </div>
                                    <div>
                                        <label className={labelStyle}>Email</label>
                                        <input type="text" name="email" value={myDetails.email} onChange={handleMyDetailsChange} className={inputStyle} placeholder="email@domain.com" />
                                    </div>
                                    <div>
                                        <label className={labelStyle}>Phone</label>
                                        <input type="text" name="phone" value={myDetails.phone} onChange={handleMyDetailsChange} className={inputStyle} placeholder="+91 00000 00000" />
                                    </div>
                                    <div className="md:col-span-2">
                                        <label className={labelStyle}>Address</label>
                                        <input type="text" name="address" value={myDetails.address} onChange={handleMyDetailsChange} className={inputStyle} placeholder="Your full address" />
                                    </div>
                                    <div className="md:col-span-2">
                                        <label className={labelStyle}>Bank Name</label>
                                        <input type="text" name="bankName" value={myDetails.bankName} onChange={handleMyDetailsChange} className={inputStyle} placeholder="State Bank of India" />
                                    </div>
                                    <div>
                                        <label className={labelStyle}>Account Number</label>
                                        <input type="text" name="accountNumber" value={myDetails.accountNumber} onChange={handleMyDetailsChange} className={inputStyle} placeholder="00000000000" />
                                    </div>
                                    <div>
                                        <label className={labelStyle}>IFSC Code</label>
                                        <input type="text" name="ifscCode" value={myDetails.ifscCode} onChange={handleMyDetailsChange} className={inputStyle} placeholder="SBIN0000000" />
                                    </div>
                                    <div className="md:col-span-2">
                                        <label className={labelStyle}>UPI Handle</label>
                                        <input type="text" name="upi" value={myDetails.upi} onChange={handleMyDetailsChange} className={inputStyle} placeholder="name@bank" />
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Section: 2 */}
                    <div className="grid gap-8">
                        <hr className="h-px border-0 bg-zinc-800 mt-10" />
                        <div className="flex flex-col md:flex-row gap-8 md:gap-14">
                            <p className="text-xl text-zinc-500 font-semibold">02</p>
                            
                            <div className="grid gap-12 w-full">
                                <h2 className="text-3xl font-medium">Bill To</h2>
                                
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-x-12 gap-y-10">
                                    <div>
                                        <label className={labelStyle}>Invoice Title</label>
                                        <input type="text" name="title" value={invoiceDetails.title} onChange={handleInvoiceChange} className={inputStyle} placeholder="INVOICE" />
                                    </div>
                                    <div>
                                        <label className={labelStyle}>Date</label>
                                        <input type="date" name="date" value={invoiceDetails.date} onChange={handleInvoiceChange} className={`${inputStyle} scheme-dark`} />
                                    </div>
                                    <div className="md:col-span-2">
                                        <label className={labelStyle}>Client / Company Name</label>
                                        <input type="text" name="companyName" value={invoiceDetails.companyName} onChange={handleInvoiceChange} className={inputStyle} placeholder="Client Name" />
                                    </div>
                                    <div className="md:col-span-2">
                                        <label className={labelStyle}>Client Address</label>
                                        <input type="text" name="companyAddress" value={invoiceDetails.companyAddress} onChange={handleInvoiceChange} className={inputStyle} placeholder="Client Address" />
                                    </div>
                                    <div>
                                        <label className={labelStyle}>GST Number (Optional)</label>
                                        <input type="text" name="gstNumber" value={invoiceDetails.gstNumber} onChange={handleInvoiceChange} className={inputStyle} placeholder="22AAAAA0000A1Z5" />
                                    </div>
                                    <div>
                                        <label className={labelStyle}>Phone (Optional)</label>
                                        <input type="text" name="companyPhone" value={invoiceDetails.companyPhone} onChange={handleInvoiceChange} className={inputStyle} placeholder="+91 00000 00000" />
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Section: 3 */}
                    <div className="grid gap-8 mb-16">
                        <hr className="h-px border-0 bg-zinc-800 mt-10" />
                        <div className="flex flex-col md:flex-row gap-8 md:gap-14">
                            <p className="text-xl text-zinc-500 font-semibold">03</p>
                            
                            <div className="grid gap-8 w-full">
                                {/* Header with Desktop Add Button (Hidden on Mobile) */}
                                <div className="flex flex-col md:flex-row justify-between md:items-center gap-4">
                                    <h2 className="text-3xl font-medium">Particulars</h2>
                                    <button onClick={addItem} className="hidden md:flex items-center justify-center gap-2 text-sm border border-zinc-700 rounded-full px-6 py-2.5 hover:bg-zinc-800 transition-colors w-full md:w-auto">
                                        <Plus size={16} /> Add Item
                                    </button>
                                </div>

                                <div className="space-y-6">
                                    {items.map((item, index) => (
                                        <div key={index} className="grid grid-cols-1 md:grid-cols-12 gap-6 md:gap-8 items-end bg-zinc-900 md:bg-transparent p-4 md:p-0 rounded-lg border border-zinc-800 md:border-none">
                                            <div className="md:col-span-1 hidden md:block text-zinc-600 text-xl pb-3 font-medium border-b border-zinc-800 text-center">
                                                {String(index + 1).padStart(2, '0')}
                                            </div>
                                            <div className="md:col-span-7">
                                                <label className="md:hidden text-sm text-zinc-500 mb-2 block">Description</label>
                                                <input type="text" value={item.particulars} onChange={(e) => handleItemChange(index, 'particulars', e.target.value)} className={inputStyle} placeholder="Web Design Services..." />
                                            </div>
                                            <div className="md:col-span-3">
                                                <label className="md:hidden text-sm text-zinc-500 mb-2 block">Amount (₹)</label>
                                                <input type="number" value={item.amount} onChange={(e) => handleItemChange(index, 'amount', e.target.value)} className={inputStyle} placeholder="0.00" />
                                            </div>
                                            <div className="md:col-span-1 flex justify-end md:justify-center pb-2">
                                                <button onClick={() => removeItem(index)} disabled={items.length === 1} className="text-zinc-600 hover:text-red-500 disabled:opacity-30 transition-colors">
                                                    <Trash2 size={24} />
                                                </button>
                                            </div>
                                        </div>
                                    ))}
                                </div>

                                {/* Mobile Add Button (Hidden on Desktop) */}
                                <div className="pt-2 md:hidden">
                                    <button 
                                        onClick={addItem} 
                                        className="flex items-center justify-center gap-2 text-sm border border-zinc-700 text-zinc-400 rounded-full px-6 py-3 hover:bg-zinc-800 hover:text-white transition-colors w-full"
                                    >
                                        <Plus size={16} /> Add another item
                                    </button>
                                </div>

                                {/* Totals Display (UPDATED: Left Aligned Currency) */}
                                <div className="mt-12 pt-8 border-t border-zinc-800 flex flex-col md:flex-row justify-between items-start md:items-end gap-6">
                                    <div className="text-zinc-500 max-w-lg">
                                        <span className="text-xs uppercase tracking-wider block mb-1">In Words</span>
                                        <p className="text-lg text-white font-medium leading-tight">
                                            {totalInWords ? `INR ${totalInWords} Only` : 'INR Zero Only'}
                                        </p>
                                    </div>
                                    <div className="text-left md:text-left">
                                        <span className="text-xs uppercase tracking-wider text-zinc-500 block mb-1">Total Amount</span>
                                        <p className="text-4xl md:text-5xl font-medium text-white">
                                            ₹{totalAmount.toLocaleString('en-IN')}
                                        </p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Section: Actions */}
                    <div className="grid gap-8">
                        <hr className="h-px border-0 bg-zinc-800 mt-10" />
                        <div className="flex flex-col md:flex-row gap-8 md:gap-14">
                            {/* UPDATED: Hidden entirely on mobile, visible on md screens with 0 opacity to keep alignment */}
                            <p className="hidden md:block text-xl text-zinc-500 font-semibold opacity-0">04</p>
                            
                            <div className="flex flex-col w-full pt-4">
                                
                                {/* NEW: Checkboxes */}
                                <div className="flex flex-wrap gap-6 mb-8">
                                    <label className="flex items-center gap-3 text-zinc-300 cursor-pointer text-sm uppercase tracking-wider font-medium">
                                        <input 
                                            type="checkbox" 
                                            checked={downloadOptions.excel}
                                            onChange={(e) => setDownloadOptions({...downloadOptions, excel: e.target.checked})}
                                            className="w-5 h-5 accent-zinc-500 bg-zinc-900 border-zinc-700 rounded cursor-pointer" 
                                        />
                                        Download as Excel
                                    </label>
                                    <label className="flex items-center gap-3 text-zinc-300 cursor-pointer text-sm uppercase tracking-wider font-medium">
                                        <input 
                                            type="checkbox" 
                                            checked={downloadOptions.pdf}
                                            onChange={(e) => setDownloadOptions({...downloadOptions, pdf: e.target.checked})}
                                            className="w-5 h-5 accent-zinc-500 bg-zinc-900 border-zinc-700 rounded cursor-pointer" 
                                        />
                                        Download as PDF
                                    </label>
                                </div>

                                {/* UPDATED: Buttons Layout */}
                                <div className="flex flex-row gap-4 md:gap-6 w-full">
                                    <button 
                                        onClick={handleReset} 
                                        title="Reset Fields"
                                        className="w-16 md:w-1/4 flex-shrink-0 flex items-center justify-center md:gap-3 bg-transparent text-white border border-zinc-700 py-4 md:py-5 rounded-full text-lg font-medium hover:bg-zinc-800 hover:border-zinc-600 transition-all"
                                    >
                                        <RotateCcw size={20} />
                                        {/* Hidden on mobile, visible on desktop */}
                                        <span className="hidden md:inline">Reset</span> 
                                    </button>
                                    <button 
                                        onClick={generateInvoice} 
                                        className="w-full md:w-3/4 flex-grow bg-white text-black py-4 md:py-5 rounded-full text-lg md:text-xl font-medium hover:bg-zinc-200 transition-all"
                                    >
                                        Generate Invoice
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>

                </div>
            </div>
        </div>
    );
}